import { AxiosError } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { TossMaterialFile } from '../../../../../api/model';
import {
  GetTossMaterialCategoriesDownloadQueryResult,
  useGetTossMaterialCategoriesDownload,
  useGetTossMaterialFileDownload,
  usePostTossMaterialCategoryUpload,
} from '../../../../../api/toss-materials/toss-materials';
import { useDefaultOptions, useOptionsForBinary } from '../../../../../hooks/api';
import { executeDownload } from '../../../../../utils/download';
import BlobErrorModal from '../../../../modules/BlobErrorModal';
import ErrorModal from '../../../../modules/ErrorModal';
import Modal from '../../../../modules/Modal';
import OkModal from '../../../../modules/OkModal';
import SpinnerModal from '../../../../modules/SpinnerModal';
import { CancelButton } from '../FormElements';

interface TextInputModalProps {
  label: string;
  defaultValue: string | undefined;
  isTextArea: boolean;
  cancel: () => void;
  ok: (nextValue: string) => void;
}

/**
 * テキスト入力可能なモーダル
 * @param param0
 * @returns
 */
function TextInputModal({ label, defaultValue, isTextArea, cancel, ok }: TextInputModalProps) {
  // 言語情報
  const { t } = useTranslation();

  // 入力されたテキスト
  const [value, setValue] = useState(defaultValue ?? '');

  return (
    <Modal
      active
      additionalClassName="mtsp popupfr600"
      displayCloseButton={false}
      onClose={() => {
        /* Do nothing */
      }}
    >
      <div className="formarea2">
        <label className="pl10">{label}</label>
        {isTextArea ? (
          <textarea
            defaultValue={defaultValue}
            style={{ width: '100%' }}
            onChange={(e) => setValue((e.target as HTMLTextAreaElement).value)}
            rows={10}
          />
        ) : (
          <input
            type="text"
            defaultValue={defaultValue}
            onChange={(e) => setValue((e.target as HTMLInputElement).value)}
          />
        )}
      </div>
      <div className="btnfr btnfr2 mb0">
        <div>
          <span className="btn2 btnbg-g mr10" onClick={cancel}>
            <span>{t('キャンセル')}</span>
          </span>
          <input type="button" className="red-button" value={t('OK').toString()} onClick={() => ok(value)} />
        </div>
      </div>
    </Modal>
  );
}

interface DownloadLiProps {
  productionId: number;
  materialFile: TossMaterialFile;
  setDisplayContextMenu: (next: boolean) => void;
}

/**
 * 押下でダウンロード可能な <li>...</li>
 * @param param0
 * @returns
 */
function DownloadLi({ productionId, materialFile, setDisplayContextMenu }: DownloadLiProps) {
  // 言語情報
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // ダウンロード API
  const options = useOptionsForBinary(language);
  const { refetch, error, isError, isRefetching, isFetching } = useGetTossMaterialFileDownload(
    productionId,
    materialFile.toss_material_file_id,
    { file_name: materialFile.file_name },
    options
  );

  // エラーを表示するか
  const [displayError, setDisplayError] = useState(false);

  useEffect(() => {
    setDisplayError(isError);
  }, [isError]);

  // ファイル名や ID が設定されていない場合は何も表示しない
  if (materialFile.toss_material_file_id == null || materialFile.file_name == null) {
    return null;
  }

  return (
    <li
      onClick={async () => {
        // コンテキストメニューを閉じる
        setDisplayContextMenu(false);

        // API を実行
        try {
          const response = await refetch();
          if (response.data != null) {
            executeDownload(response.data);
          }
        } catch {
          // Do nothing
        }
      }}
    >
      <i className="fa-solid fa-download" />
      {t('ダウンロード')}
      {/* エラー表示 */}
      {displayError && error != null && <OkModal onClickOk={() => setDisplayError(false)}>{error.message}</OkModal>}
      {/* スピナーモーダルの表示 */}
      {(isFetching || isRefetching) && <SpinnerModal />}
    </li>
  );
}

interface DroppableTdMainProps {
  productionId: number;
  tabIndex: number;
  editable: boolean;
  downloadable: boolean;
  displayCommentArea: boolean;
  materialFile: TossMaterialFile | undefined;
  getPresentFilenames: () => string[];
  onDropSucceeded: (files: TossMaterialFile[]) => void;
  onClickComment: () => void;
  onClickChangeMaterialFileName: () => void;
  onClickDelete: () => void;
  onClickDeleteRow: (() => void) | undefined;
}

/**
 * ファイルドロップ可能な <td>...</td> のメイン部分
 * @param param0
 * @returns
 */
function DroppableTdMain({
  productionId,
  tabIndex,
  editable,
  displayCommentArea,
  materialFile,
  getPresentFilenames,
  onDropSucceeded,
  onClickComment,
  onClickChangeMaterialFileName,
  onClickDelete,
  onClickDeleteRow,
}: DroppableTdMainProps) {
  // 言語情報
  const {
    i18n: { language },
  } = useTranslation();

  // スピナーモーダルの表示
  const [displaySpinnerModal, setDisplaySpinnerModal] = useState(false);

  // ファイルアップロード API
  const options = useDefaultOptions(language);
  const { mutateAsync, error, isError } = usePostTossMaterialCategoryUpload(options);

  // エラー表示
  const [displayError, setDisplayError] = useState(false);
  useEffect(() => setDisplayError(isError), [isError]);

  // ファイル名重複エラー
  const [displayDuplicateFilenameError, setDisplayDuplicateFilenameError] = useState(false);

  // ファイルドロップされたときの動作を規定
  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const fileNames = getPresentFilenames();

      // ファイル名が重複した場合はエラー表示して中止
      if (acceptedFiles.some((x) => fileNames.includes(x.name))) {
        setDisplayDuplicateFilenameError(true);
        return;
      }

      // スピナーモーダル表示
      setDisplaySpinnerModal(true);

      try {
        // API 実行
        const response = await mutateAsync({
          productionId,
          tossMaterialCategoryId: tabIndex,
          data: { 'files[]': acceptedFiles },
        });

        // 成功したら実行
        if (response.data != null && response.data.toss_material_files != null) {
          onDropSucceeded(response.data.toss_material_files);
        }
      } catch {
        // Do nothing
      }

      // スピナーモーダル非表示
      setDisplaySpinnerModal(false);
    },
    [getPresentFilenames, mutateAsync, productionId, tabIndex, onDropSucceeded]
  );

  // Dropzone 関連のパラメータ
  const { getRootProps, isDragAccept } = useDropzone({ onDrop, disabled: !editable });

  // コンテキストメニューを表示するかどうか
  const [displayContextMenu, setDisplayContextMenu] = useState(false);

  // 言語情報
  const { t } = useTranslation();

  useEffect(() => {
    // 編集可否が変わった場合は、コンテキストメニューを閉じる
    setDisplayContextMenu(false);
  }, [editable]);

  const refContextMenu = useRef<HTMLUListElement>(null);

  return (
    <td
      {...getRootProps()}
      onContextMenu={(e) => {
        if (!(editable && displayCommentArea) && !(editable && onClickDeleteRow != null) && materialFile == null) {
          return;
        }

        setDisplayContextMenu(true);
        const closeContextMenu = (e2: MouseEvent) => {
          if (refContextMenu.current != null && !refContextMenu.current.contains(e2.target as Node)) {
            setDisplayContextMenu(false);
            document.removeEventListener('mousedown', closeContextMenu);
          }
        };
        document.addEventListener('mousedown', closeContextMenu);
        e.preventDefault();
      }}
      style={{ position: 'relative', backgroundColor: isDragAccept ? '#fAfAfA' : undefined }}
    >
      {/* ファイル名 */}
      <span className="doculistbtn">{materialFile?.file_name}</span>
      {/* コンテキストメニュー */}
      <ul
        style={displayContextMenu ? { display: 'block' } : {}}
        ref={refContextMenu}
        onClick={(e) => e.stopPropagation()}
      >
        {materialFile != null && (
          <>
            {/* ダウンロード */}
            <DownloadLi
              productionId={productionId}
              materialFile={materialFile}
              setDisplayContextMenu={setDisplayContextMenu}
            />
            {/* 資料名変更 */}
            {editable && (
              <li
                onClick={() => {
                  // コンテキストメニューを閉じる
                  setDisplayContextMenu(false);

                  // 資料名変更を実行
                  onClickChangeMaterialFileName();
                }}
              >
                <i className="fa-solid fa-pen-to-square" />
                {t('資料名変更')}
              </li>
            )}
            {/* 削除 */}
            {editable && (
              <li
                onClick={() => {
                  // コンテキストメニューを閉じる
                  setDisplayContextMenu(false);

                  // 削除実行
                  onClickDelete();
                }}
              >
                <i className="fa-solid fa-circle-xmark" />
                {t('削除')}
              </li>
            )}
          </>
        )}
        {/* 行削除 */}
        {editable && onClickDeleteRow != null && (
          <li
            onClick={() => {
              // コンテキストメニューを閉じる
              setDisplayContextMenu(false);

              // 行削除実行
              onClickDeleteRow();
            }}
          >
            <i className="fa-solid fa-list-check" />
            {t('行削除')}
          </li>
        )}
        {/* コメント */}
        {editable && displayCommentArea && (
          <li
            onClick={() => {
              // コンテキストメニューを閉じる
              setDisplayContextMenu(false);

              // コメント変更実行
              onClickComment();
            }}
          >
            <i className="fa-solid fa-comment" />
            {t('コメント')}
          </li>
        )}
      </ul>
      {/* スピナーモーダル */}
      {displaySpinnerModal && <SpinnerModal />}
      {/* エラー表示 */}
      {displayError && <ErrorModal error={error} onClickOk={() => setDisplayError(false)} />}
      {/* ファイル名重複エラー */}
      {displayDuplicateFilenameError && (
        <OkModal onClickOk={() => setDisplayDuplicateFilenameError(false)}>
          <div>{t('ファイルを保存できません。同名のファイルが存在する為、ファイル名称を変更し保存してください。')}</div>
        </OkModal>
      )}
    </td>
  );
}

interface DroppableTdProps {
  productionId: number;
  tabIndex: number;
  editable: boolean;
  file: TossMaterialFile | undefined;
  comment: string | undefined;
  getPresentFilenames: () => string[];
  onDropSucceeded: (files: TossMaterialFile[]) => void;
  onChangeComment: ((next: string | undefined) => void) | undefined;
  onChangeMaterialFileName: (next: string | undefined) => void;
  onClickDelete: () => void;
  onCickDeleteRow: (() => void) | undefined;
}

/**
 * ファイルドロップ可能な <td>...</td>
 * @param param0
 * @returns
 */
export function DroppableTd({
  productionId,
  tabIndex,
  editable,
  file,
  comment,
  getPresentFilenames,
  onDropSucceeded,
  onChangeComment,
  onChangeMaterialFileName,
  onClickDelete,
  onCickDeleteRow,
}: DroppableTdProps) {
  // 言語情報
  const { t } = useTranslation();

  // コメントモーダル
  const [displayCommentModal, setDisplayCommentModal] = useState(false);

  // 資料名モーダル
  const [displayMaterialNameModal, setDisplayMaterialNameModal] = useState(false);

  // ファイル名重複エラー
  const [displayDuplicateFilenameError, setDisplayDuplicateFilenameError] = useState(false);

  return (
    <>
      {/* テーブルセル本体 */}
      <DroppableTdMain
        productionId={productionId}
        tabIndex={tabIndex}
        editable={editable}
        downloadable={file != null}
        materialFile={file}
        displayCommentArea={onChangeComment != null}
        getPresentFilenames={getPresentFilenames}
        onDropSucceeded={(files) => onDropSucceeded(files)}
        onClickComment={() => setDisplayCommentModal(true)}
        onClickChangeMaterialFileName={() => setDisplayMaterialNameModal(true)}
        onClickDelete={onClickDelete}
        onClickDeleteRow={onCickDeleteRow}
      />
      {/* コメントモーダル */}
      {displayCommentModal && onChangeComment != null && (
        <TextInputModal
          label={t('コメント').toString()}
          defaultValue={comment}
          isTextArea
          cancel={() => setDisplayCommentModal(false)}
          ok={(next) => {
            onChangeComment(next);
            setDisplayCommentModal(false);
          }}
        />
      )}
      {/* 資料名モーダル */}
      {displayMaterialNameModal && file != null && (
        <TextInputModal
          label={t('資料').toString()}
          defaultValue={file.file_name}
          isTextArea={false}
          cancel={() => setDisplayMaterialNameModal(false)}
          ok={(next) => {
            // ファイル名が被っている場合はエラー表示（モーダルは閉じない）
            const presentFilenames = getPresentFilenames();
            if (next !== file.file_name && presentFilenames.includes(next)) {
              setDisplayDuplicateFilenameError(true);
              return;
            }

            // ファイル名が被っていない場合

            // ファイル名変更を実施し、モーダルを閉じる
            onChangeMaterialFileName(next);
            setDisplayMaterialNameModal(false);
          }}
        />
      )}
      {/* ファイル名重複エラー */}
      {displayDuplicateFilenameError && (
        <OkModal onClickOk={() => setDisplayDuplicateFilenameError(false)}>
          <div>{t('ファイルを保存できません。同名のファイルが存在する為、ファイル名称を変更し保存してください。')}</div>
        </OkModal>
      )}
    </>
  );
}

interface InnerTabCommonNaviProps {
  productionId: number;
  tabIndex: number;
  editable: boolean;
  open: () => void;
  close: () => void;
  edit: () => Promise<void> | void;
  cancel: () => Promise<void> | void;
}

/**
 * トス資料の子タブのナビゲーションバー
 * @param param0
 * @returns
 */
export function InnerTabCommonNavi({
  productionId,
  tabIndex,
  editable,
  open,
  close,
  edit,
  cancel,
}: InnerTabCommonNaviProps) {
  // 言語情報
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // ダウンロード API
  const optionsForBinary = useOptionsForBinary(language);
  const { refetch, error } = useGetTossMaterialCategoriesDownload<
    GetTossMaterialCategoriesDownloadQueryResult,
    AxiosError<Blob>
  >(productionId, tabIndex, optionsForBinary);

  // ダウンロード API のエラーを表示するかどうか
  const [displayDownloadError, setDisplayDownloadError] = useState(false);

  // スピナーモーダルを表示するかどうか
  const [displaySpinnerModal, setDisplaySpinnerModal] = useState(false);

  useEffect(() => {
    setDisplayDownloadError(error != null);
  }, [error]);

  // ダウンロードの実行
  const download = useCallback(async () => {
    setDisplaySpinnerModal(true);
    const response = await refetch();
    setDisplaySpinnerModal(false);
    if (response.data != null) {
      executeDownload(response.data);
    }
  }, [refetch]);

  return (
    <>
      <div className="master-navi navisep">
        <ul>
          {/* すべてを閉じるボタン */}
          <li className="master-navibtn1">
            <button type="button" className="text-button" onClick={close}>
              <i className="fa-solid fa-chevron-up" />
              {t('すべて閉じる')}
            </button>
          </li>
          {/* すべてを開くボタン */}
          <li className="master-navibtn2">
            <button type="button" className="text-button" onClick={open}>
              <i className="fa-solid fa-chevron-down" />
              {t('すべて開く')}
            </button>
          </li>
          {/* 編集ボタン */}
          <li className="master-navibtn3">
            <button type="button" className="text-button" onClick={edit} disabled={editable}>
              <i className="fa-solid fa-pen-to-square" />
              {t('編集')}
            </button>
          </li>
          {/* キャンセルボタン */}
          <li className="master-navibtn4">
            <CancelButton editable={editable} cancel={cancel} />
          </li>
        </ul>
        {/* 資料一括ダウンロードボタン */}
        <p className="dwl">
          <button type="button" className="text-button" onClick={download} disabled={editable}>
            <i className="fa-solid fa-download" />
            {t('資料一括ダウンロード')}
          </button>
        </p>
      </div>
      {/* スピナーモーダル */}
      {displaySpinnerModal && <SpinnerModal />}
      {/* ダウンロードエラー */}
      {displayDownloadError && <BlobErrorModal error={error} onClickOk={() => setDisplayDownloadError(false)} />}
    </>
  );
}
